import wretch from 'wretch';

import { type LoadServerCxModuleOptions } from '@cx-modules/client';
import { configureErrorCatchers } from '@cx-modules/errors';

import { isMissingUsRegionContext } from './region-context';
import type { OddsGridApiErrorResponse, OddsGridApiRequestParams, OddsGridApiResponseData } from './types';
export * from './region-context';
export * from './types';

/**
 * Fetches odds grid data from the CX API grid endpoint
 * This service is shared between odds-grid and odds-grid-vertical modules
 */
export const fetchOddsGridApiData = async (
  dataApiEndpoint: string | undefined,
  params: OddsGridApiRequestParams,
  options?: LoadServerCxModuleOptions,
): Promise<OddsGridApiResponseData | OddsGridApiErrorResponse | null> => {
  if (!dataApiEndpoint) {
    throw new Error('fetchOddsGridApiData requires a dataApiEndpoint');
  }
  const { apiKey, countryCode, marketId, overrideBookies, repub, subdivision } = params;

  if (isMissingUsRegionContext({ countryCode, subdivision })) {
    return {
      error: 'MISSING_REGION_CONTEXT',
      message: 'US countryCode requires a subdivisionCode.',
    };
  }

  const url = new URL(`${dataApiEndpoint}/v1/grid/${marketId}`);

  if (overrideBookies && overrideBookies.length > 0) {
    url.searchParams.append('overrideBookies', overrideBookies.join(','));
  }

  const headers = {
    accept: 'application/json',
    'content-type': 'application/json',
    repub,
    ...(apiKey ? { 'api-key': apiKey } : {}),
    ...(countryCode ? { 'country-code': countryCode } : {}),
    ...(subdivision ? { 'subdivision-code': subdivision } : {}),
    ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
  };

  const client = configureErrorCatchers(
    wretch(url.toString(), {
      headers,
    }),
    options,
  );

  return client
    .get()
    .notFound(() => null)
    .json<OddsGridApiResponseData>();
};

/**
 * Type guard to check if the API response is an error response
 */
export const isOddsGridApiErrorResponse = (
  response: OddsGridApiResponseData | OddsGridApiErrorResponse | null,
): response is OddsGridApiErrorResponse => {
  return response !== null && 'error' in response;
};

// Legacy function names for backwards compatibility
export const fetchOddsGridData = fetchOddsGridApiData;
export const fetchOddsGridVerticalData = fetchOddsGridApiData;
